/**
 * Forma do símbolo das estações: uma GOTA — bulbo circular embaixo, ponta
 * arredondada em cima.
 *
 * A escolha não é decorativa. O ponto circular já é das sedes municipais, e
 * `fires/flame.ts` diz, na própria descrição, que a chama foi desenhada "para
 * não confundir com o ponto circular das sedes municipais (nem com uma gota)"
 * — ou seja, a gota é a silhueta que sobra livre no mapa, e ela ainda por cima
 * diz sozinha do que a camada trata.
 *
 * A geometria fica aqui, e não importada dos focos, para os módulos seguirem
 * independentes (ver "Desenvolvimento em paralelo" em `docs/ARQUITETURA.md`).
 *
 * Unidades: múltiplos do raio base, origem na posição da estação, y para CIMA.
 */

export interface Gota {
  /** Raio do bulbo. */
  r1: number;
  /** Raio de arredondamento da ponta. */
  r2: number;
  /** Distância do centro do bulbo à ponta. */
  h: number;
}

/** Proporção da gota: bulbo cheio embaixo, ponta fina em cima. */
export const GOTA: Gota = { r1: 0.78, r2: 0.1, h: 1.72 };

/**
 * Contorno da gota como polígono: arco do bulbo por baixo, reta tangente,
 * arco da ponta por cima, reta tangente de volta. As tangentes saem do ângulo
 * em que a reta encosta nos dois círculos, então não há quina.
 */
export function gotaPoligono(s: Gota = GOTA, arcos = 30, ponta = 8): Array<[number, number]> {
  const b = (s.r1 - s.r2) / s.h;
  const th = Math.asin(Math.min(1, Math.max(-1, b)));
  const pts: Array<[number, number]> = [];

  // Bulbo: de th (direita) descendo por baixo até π − th (esquerda).
  for (let i = 0; i <= arcos; i++) {
    const a = th - ((Math.PI + 2 * th) * i) / arcos;
    pts.push([s.r1 * Math.cos(a), s.r1 * Math.sin(a)]);
  }
  // Ponta: de π − th (esquerda) por cima até th (direita).
  for (let i = 0; i <= ponta; i++) {
    const a = Math.PI - th - ((Math.PI - 2 * th) * i) / ponta;
    pts.push([s.r2 * Math.cos(a), s.h + s.r2 * Math.sin(a)]);
  }
  return pts;
}

const POLI = gotaPoligono();

/** Maior afastamento da gota (unidades do raio), para dimensionar o sprite. */
export const GOTA_EXTENT = Math.max(...POLI.map(([x, y]) => Math.max(Math.abs(x), Math.abs(y))));

/** Caminho SVG (y para BAIXO, como no canvas e no SVG). */
export const GOTA_SVG =
  POLI.map(([x, y], i) => `${i ? 'L' : 'M'}${Math.round(x * 1000) / 1000} ${Math.round(-y * 1000) / 1000}`).join('') +
  'Z';

/** Path2D em unidades do raio, y para baixo. Criado sob demanda (só no navegador). */
let path: Path2D | null = null;
export function gotaPath(): Path2D {
  path ??= new Path2D(GOTA_SVG);
  return path;
}
